import { useState } from "react";
import { X, Phone, Mail, Send } from "lucide-react";

const PHONE = "+18187937558";
const EMAIL = "office@ellantesusa.com";

export default function RequestDemoModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", practice: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!open) return null;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          practice_name: form.practice,
          message: form.message,
          inquiry_type: "demo_request",
        }),
      });
      setSent(true);
      // Also open mailto
      window.location.href = `mailto:${EMAIL}?subject=Demo Request from ${form.name}&body=Name: ${form.name}%0AEmail: ${form.email}%0APhone: ${form.phone}%0APractice: ${form.practice}%0A%0A${form.message}`;
    } catch {
      setSent(true);
      window.location.href = `mailto:${EMAIL}?subject=Demo Request from ${form.name}&body=Name: ${form.name}%0AEmail: ${form.email}%0APhone: ${form.phone}%0APractice: ${form.practice}%0A%0A${form.message}`;
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" data-testid="demo-modal-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative w-full max-w-lg bg-[#141418] border border-white/10 p-8 lg:p-10 z-10"
        data-testid="demo-modal"
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/30 hover:text-white transition-colors"
          data-testid="demo-modal-close"
        >
          <X size={18} />
        </button>

        {/* Header */}
        <p
          className="text-xs tracking-[0.3em] uppercase mb-3"
          style={{ color: "#D4AF37", fontFamily: "'Manrope', sans-serif", fontWeight: 600 }}
        >
          Request a Demo
        </p>
        <h3
          className="text-2xl font-light text-white mb-6"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Get in Touch
        </h3>

        {/* Quick Actions */}
        <div className="flex gap-3 mb-8">
          <a
            href={`tel:${PHONE}`}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#D4AF37] text-[#0A0A0C] text-xs tracking-[0.1em] uppercase font-semibold hover:bg-[#F0C95C] transition-colors"
            style={{ fontFamily: "'Manrope', sans-serif" }}
            data-testid="demo-call-btn"
          >
            <Phone size={14} />
            Call Now
          </a>
          <a
            href={`mailto:${EMAIL}`}
            className="flex-1 flex items-center justify-center gap-2 py-3 border border-[#D4AF37]/50 text-[#D4AF37] text-xs tracking-[0.1em] uppercase font-medium hover:bg-[#D4AF37]/10 transition-colors"
            style={{ fontFamily: "'Manrope', sans-serif" }}
            data-testid="demo-email-btn"
          >
            <Mail size={14} />
            Email Us
          </a>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-[10px] text-white/25 uppercase tracking-widest" style={{ fontFamily: "'Manrope', sans-serif" }}>
            or leave your details
          </span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        {sent ? (
          <div className="text-center py-8" data-testid="demo-success">
            <p className="text-lg text-white mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Thank You!
            </p>
            <p className="text-sm text-white/50" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Your inquiry has been submitted. We'll be in touch shortly.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="demo-form">
            <div className="grid grid-cols-2 gap-3">
              <input
                name="name"
                placeholder="Full Name *"
                required
                value={form.name}
                onChange={handleChange}
                className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-[#D4AF37]/40"
                style={{ fontFamily: "'Manrope', sans-serif" }}
                data-testid="demo-input-name"
              />
              <input
                name="email"
                type="email"
                placeholder="Email *"
                required
                value={form.email}
                onChange={handleChange}
                className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-[#D4AF37]/40"
                style={{ fontFamily: "'Manrope', sans-serif" }}
                data-testid="demo-input-email"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                name="phone"
                placeholder="Phone"
                value={form.phone}
                onChange={handleChange}
                className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-[#D4AF37]/40"
                style={{ fontFamily: "'Manrope', sans-serif" }}
                data-testid="demo-input-phone"
              />
              <input
                name="practice"
                placeholder="Practice Name"
                value={form.practice}
                onChange={handleChange}
                className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-[#D4AF37]/40"
                style={{ fontFamily: "'Manrope', sans-serif" }}
                data-testid="demo-input-practice"
              />
            </div>
            <textarea
              name="message"
              placeholder="Tell us about your practice and needs..."
              rows={3}
              value={form.message}
              onChange={handleChange}
              className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-[#D4AF37]/40 resize-none"
              style={{ fontFamily: "'Manrope', sans-serif" }}
              data-testid="demo-input-message"
            />
            <button
              type="submit"
              disabled={sending}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#D4AF37] text-[#0A0A0C] text-xs tracking-[0.15em] uppercase font-semibold hover:bg-[#F0C95C] transition-colors disabled:opacity-50"
              style={{ fontFamily: "'Manrope', sans-serif" }}
              data-testid="demo-submit-btn"
            >
              <Send size={14} />
              {sending ? "Sending..." : "Submit Request"}
            </button>
          </form>
        )}

        {/* Contact info */}
        <div className="mt-6 pt-4 border-t border-white/5 flex flex-wrap items-center justify-center gap-4">
          <a
            href={`tel:${PHONE}`}
            className="text-xs text-white/30 hover:text-[#D4AF37] transition-colors flex items-center gap-1.5"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            <Phone size={10} /> (818) 793-7558
          </a>
          <a
            href={`mailto:${EMAIL}`}
            className="text-xs text-white/30 hover:text-[#D4AF37] transition-colors flex items-center gap-1.5"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            <Mail size={10} /> office@ellantesusa.com
          </a>
        </div>
      </div>
    </div>
  );
}
